import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
    X, Heart, MapPin, Shield, Zap, Navigation, Package,
    Users, Target, ArrowRight, Sparkles, CheckCircle2,
    Lightbulb, Cpu, Activity, Radio, Layers, Compass,
    ChevronRight, ArrowUpRight
} from "lucide-react";
import SectionHeader from "../SectionHeader";
import { useTranslation } from "react-i18next";
import fa from "./locales/HealthLocationInnovation-fa.json";
import en from "./locales/HealthLocationInnovation-en.json";
import ar from "./locales/HealthLocationInnovation-ar.json";
import tr from "./locales/HealthLocationInnovation-tr.json";

// ─── Floating Particle ────────────────────────────────────────────────────────
const FloatingParticle: React.FC<{ x: number; delay: number; color: string }> = ({ x, delay, color }) => (
    <motion.div
        className="absolute w-1.5 h-1.5 rounded-full pointer-events-none"
        style={{ left: `${x}%`, bottom: "8%", backgroundColor: color }}
        animate={{ y: [0, -36, 0], opacity: [0, 0.8, 0], scale: [0, 1.2, 0] }}
        transition={{ duration: 2.2, delay, repeat: Infinity, ease: "easeInOut" }}
    />
);

// ─── Pulse Rings ──────────────────────────────────────────────────────────────
const PulseRings: React.FC<{ color: string; count?: number }> = ({ color, count = 3 }) => (
    <>
        {Array.from({ length: count }).map((_, i) => (
            <motion.div
                key={i}
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{ border: `1.5px solid ${color}` }}
                initial={{ opacity: 0.7, scale: 1 }}
                animate={{ opacity: 0, scale: 2 }}
                transition={{ duration: 2.4, delay: i * 0.6, repeat: Infinity, ease: "easeOut" }}
            />
        ))}
    </>
);

// ─── Shimmer Line ──────────────────────────────────────────────────────────────
const ShimmerLine: React.FC<{ active: boolean; color: string }> = ({ active, color }) => (
    <motion.div
        className="absolute top-0 left-0 right-0 h-[2px] rounded-full pointer-events-none overflow-hidden"
        style={{ background: `linear-gradient(90deg, transparent 0%, ${color} 50%, transparent 100%)` }}
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: active ? 1 : 0, scaleX: active ? 1 : 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
    />
);

// ─── Tilt Card ───────────────────────────────────────────────────────────────
const TiltCardWrapper: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => {
    const ref = useRef<HTMLDivElement>(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const rotateX = useTransform(y, [-0.5, 0.5], [5, -5]);
    const rotateY = useTransform(x, [-0.5, 0.5], [-5, 5]);
    const springConfig = { stiffness: 220, damping: 22 };
    const springRotateX = useSpring(rotateX, springConfig);
    const springRotateY = useSpring(rotateY, springConfig);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        x.set((e.clientX - rect.left) / rect.width - 0.5);
        y.set((e.clientY - rect.top) / rect.height - 0.5);
    };
    const handleMouseLeave = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <motion.div
            ref={ref}
            className={className}
            style={{ rotateX: springRotateX, rotateY: springRotateY, transformStyle: "preserve-3d" }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            {children}
        </motion.div>
    );
};

// ─── Magnetic Button ──────────────────────────────────────────────────────────
const MagneticBtn: React.FC<{
    children: React.ReactNode;
    onClick?: () => void;
    className?: string;
    style?: React.CSSProperties;
    type?: "button" | "submit";
}> = ({ children, onClick, className = "", style, type = "button" }) => {
    const ref = useRef<HTMLButtonElement>(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const sx = useSpring(x, { stiffness: 180, damping: 16 });
    const sy = useSpring(y, { stiffness: 180, damping: 16 });
    const handleMove = (e: React.MouseEvent<HTMLButtonElement>) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        x.set((e.clientX - rect.left - rect.width / 2) * 0.2);
        y.set((e.clientY - rect.top - rect.height / 2) * 0.2);
    };
    const handleLeave = () => { x.set(0); y.set(0); };
    return (
        <motion.button
            ref={ref} type={type} style={{ x: sx, y: sy, ...style }}
            className={className} onClick={onClick}
            onMouseMove={handleMove} onMouseLeave={handleLeave}
            whileTap={{ scale: 0.96 }}
        >
            {children}
        </motion.button>
    );
};

// ─── Color Map ────────────────────────────────────────────────────────────────
const COLOR_MAP: Record<string, {
    from: string;
    to: string;
    text: string;
    border: string;
    bg: string;
    glow: string;
    lightBg: string;
}> = {
    blue:   { from: "#0072CE", to: "#06b6d4", text: "#005a9c", border: "rgba(0,114,206,0.25)", bg: "rgba(239,246,255,0.9)", lightBg: "#eff6ff", glow: "rgba(0,114,206,0.2)" },
    purple: { from: "#8b5cf6", to: "#6366f1", text: "#5b21b6", border: "rgba(139,92,246,0.25)", bg: "rgba(245,243,255,0.9)", lightBg: "#f5f3ff", glow: "rgba(139,92,246,0.2)" },
    orange: { from: "#f97316", to: "#ea580c", text: "#c2410c", border: "rgba(249,115,22,0.25)", bg: "rgba(255,247,237,0.9)", lightBg: "#fff7ed", glow: "rgba(249,115,22,0.2)" },
    green:  { from: "#10b981", to: "#0d9488", text: "#065f46", border: "rgba(16,185,129,0.25)", bg: "rgba(236,253,245,0.9)", lightBg: "#ecfdf5", glow: "rgba(16,185,129,0.2)" },
};

// ─── Live ECG Heartbeat Bars ──────────────────────────────────────────────────
const LiveECGAnimation: React.FC = () => {
    const heights = [6, 14, 8, 32, 10, 24, 8, 18, 6, 28, 12, 16];
    return (
        <div className="h-12 w-full rounded-2xl bg-gradient-to-r from-rose-50/80 via-pink-50/50 to-rose-50/80 border border-rose-100/70 p-2 flex items-center justify-between gap-1">
            <div className="flex items-center gap-1.5 text-rose-600">
                <Activity className="w-3.5 h-3.5 animate-pulse text-rose-500" />
                <span className="text-[11px] font-semibold tracking-tight text-rose-600">Live ECG</span>
            </div>
            <div className="h-8 flex items-end gap-1 px-1">
                {heights.map((h, i) => (
                    <motion.div
                        key={i}
                        className="w-1.5 rounded-full"
                        style={{ background: "linear-gradient(to top, #f43f5e, #fb7185)" }}
                        animate={{ height: [4, h, 4], opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.09, ease: "easeInOut" }}
                    />
                ))}
            </div>
        </div>
    );
};

// ─── Spatial Radar Wave ───────────────────────────────────────────────────────
const SpatialRadarAnimation: React.FC = () => (
    <div className="h-12 w-full rounded-2xl bg-gradient-to-r from-sky-50/80 via-blue-50/50 to-sky-50/80 border border-sky-100/70 p-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-blue-600">
            <Radio className="w-3.5 h-3.5 animate-pulse text-blue-500" />
            <span className="text-[11px] font-semibold tracking-tight text-blue-600">Sub-meter RTLS</span>
        </div>
        <div className="relative w-20 h-8 overflow-hidden rounded-xl flex items-center justify-center">
            {[1, 2, 3].map((i) => (
                <motion.div
                    key={i}
                    className="absolute rounded-full border border-blue-400/50"
                    style={{ width: i * 20, height: i * 20 }}
                    animate={{ scale: [0.6, 1.4], opacity: [0.9, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.45, ease: "easeOut" }}
                />
            ))}
            <div className="w-2.5 h-2.5 rounded-full bg-blue-600 shadow-[0_0_10px_rgba(37,99,235,0.8)] z-10" />
        </div>
    </div>
);

// ─── Service Card ─────────────────────────────────────────────────────────────
interface ServiceCardProps {
    service: { icon: React.ElementType; title: string; shortDesc: string; gradient: string; glowColor: string; accentColor: string };
    index: number;
    isRTL: boolean;
    textAlign: string;
}
const ServiceCard: React.FC<ServiceCardProps> = ({ service, index, isRTL, textAlign }) => {
    const [hovered, setHovered] = useState(false);
    const [particles, setParticles] = useState<{ id: number; x: number }[]>([]);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const Icon = service.icon;

    const spawnParticles = () => {
        const ps = Array.from({ length: 4 }, (_, i) => ({ id: Date.now() + i, x: 15 + Math.random() * 70 }));
        setParticles(ps);
        timerRef.current = setTimeout(() => setParticles([]), 2200);
    };
    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

    return (
        <motion.div
            className="relative group h-full"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.12 }}
            viewport={{ once: true }}
            whileHover={{ y: -6 }}
            onMouseEnter={() => { setHovered(true); spawnParticles(); }}
            onMouseLeave={() => setHovered(false)}
        >
            {/* Ambient Backlight Glow */}
            <div
                className={`absolute inset-0 bg-gradient-to-br ${service.gradient} rounded-3xl blur-2xl transition-opacity duration-500`}
                style={{ opacity: hovered ? 0.18 : 0.04 }}
            />

            <div
                className="relative h-full overflow-hidden rounded-3xl flex flex-col justify-between p-6 transition-all duration-300"
                style={{
                    background: hovered ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.82)",
                    border: hovered ? "1px solid rgba(0,114,206,0.3)" : "1px solid rgba(226,232,240,0.8)",
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    boxShadow: hovered ? "0 20px 40px -10px rgba(0,114,206,0.12), 0 0 0 1px rgba(0,114,206,0.05)" : "0 4px 20px rgba(0,0,0,0.03)",
                }}
            >
                <ShimmerLine active={hovered} color={service.accentColor} />

                {/* Micro Particles */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    {particles.map((p) => <FloatingParticle key={p.id} x={p.x} delay={0} color={service.accentColor} />)}
                </div>

                <div>
                    <div className="flex items-center justify-between mb-4">
                        <div
                            className={`p-3 rounded-2xl text-white shadow-md transition-transform duration-300 ${hovered ? "scale-110" : ""}`}
                            style={{ background: `linear-gradient(135deg, ${service.accentColor}, #0284c7)` }}
                        >
                            <Icon className="w-5 h-5" />
                        </div>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                            0{index + 1}
                        </span>
                    </div>

                    <h3 className={`text-lg font-bold text-slate-900 mb-2.5 transition-colors ${textAlign} ${hovered ? "text-[#0072CE]" : ""}`}>
                        {service.title}
                    </h3>
                    <p className={`text-sm text-slate-600 leading-relaxed ${textAlign}`}>
                        {service.shortDesc}
                    </p>
                </div>

                <div className={`mt-5 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-semibold ${textAlign}`}>
                    <span className="text-slate-400 group-hover:text-[#0072CE] transition-colors">
                        {isRTL ? "قابلیت یکپارچه‌سازی" : "System Integration"}
                    </span>
                    <ArrowUpRight className={`w-4 h-4 text-slate-300 group-hover:text-[#0072CE] transition-all transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${isRTL ? "rotate-90" : ""}`} />
                </div>
            </div>
        </motion.div>
    );
};

// ─── Tech Card (Delband / Indoora) ────────────────────────────────────────────
const TechCard: React.FC<{
    type: "delband" | "indoora";
    title: string; subtitle: string; description: string;
    textAlign: string;
    t: any;
}> = ({ type, title, subtitle, description, textAlign, t }) => {
    const [hovered, setHovered] = useState(false);
    const isDelband = type === "delband";
    const accent = isDelband
        ? { from: "#f43f5e", to: "#ec4899", color: "#f43f5e", bg: "rgba(254,242,242,0.85)", badge: "Delband Wearables" }
        : { from: "#0072CE", to: "#06b6d4", color: "#0072CE", bg: "rgba(239,246,255,0.85)", badge: "Indoora RTLS Platform" };

    return (
        <TiltCardWrapper className="h-full">
            <motion.div
                className="relative group h-full"
                whileHover={{ y: -4 }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                {/* Glow */}
                <div
                    className="absolute inset-0 rounded-3xl blur-2xl transition-opacity duration-500"
                    style={{ background: `linear-gradient(135deg, ${accent.from}30, ${accent.to}30)`, opacity: hovered ? 0.6 : 0.2 }}
                />

                <div
                    className="relative overflow-hidden rounded-3xl p-6 sm:p-7 flex flex-col justify-between h-full transition-all duration-300"
                    style={{
                        background: hovered ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.85)",
                        border: hovered ? `1px solid ${accent.from}40` : "1px solid rgba(226,232,240,0.8)",
                        backdropFilter: "blur(24px)",
                        WebkitBackdropFilter: "blur(24px)",
                        boxShadow: hovered ? `0 20px 40px -10px ${accent.from}20` : "0 4px 24px rgba(0,0,0,0.04)",
                    }}
                >
                    <ShimmerLine active={hovered} color={accent.from} />

                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <motion.div
                                className="relative p-3.5 rounded-2xl text-white shadow-lg"
                                style={{ background: `linear-gradient(135deg, ${accent.from}, ${accent.to})` }}
                                animate={hovered ? { scale: 1.06, rotate: isDelband ? 6 : -6 } : { scale: 1, rotate: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                {hovered && <PulseRings color={`${accent.from}50`} count={2} />}
                                {isDelband ? (
                                    <Heart className="w-6 h-6" fill="currentColor" fillOpacity={0.25} />
                                ) : (
                                    <MapPin className="w-6 h-6" />
                                )}
                            </motion.div>
                            <span
                                className="text-[11px] font-bold px-3 py-1 rounded-full border shadow-sm"
                                style={{ background: accent.bg, color: accent.color, borderColor: `${accent.from}30` }}
                            >
                                {accent.badge}
                            </span>
                        </div>

                        <h3 className={`text-xl font-extrabold text-slate-900 mb-1 transition-colors ${textAlign}`}>
                            {title}
                        </h3>
                        <p className={`text-xs font-semibold mb-3 ${textAlign}`} style={{ color: accent.color }}>
                            {subtitle}
                        </p>
                        <p className={`text-sm text-slate-600 leading-relaxed mb-6 ${textAlign}`}>
                            {description}
                        </p>
                    </div>

                    <div>
                        {isDelband ? <LiveECGAnimation /> : <SpatialRadarAnimation />}
                    </div>
                </div>
            </motion.div>
        </TiltCardWrapper>
    );
};

// ─── CTA Button ───────────────────────────────────────────────────────────────
const CTAButton: React.FC<{ label: string; isRTL: boolean; onClick: () => void }> = ({ label, isRTL, onClick }) => {
    const [hovered, setHovered] = useState(false);

    return (
        <MagneticBtn
            onClick={onClick}
            className="relative px-9 py-4 rounded-full text-white font-bold text-base overflow-hidden cursor-pointer shadow-xl shadow-blue-500/20 group"
        >
            {/* Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0072CE] via-[#0284c7] to-[#06b6d4] transition-all duration-300 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#005a9c] via-[#0072CE] to-[#0284c7] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Shimmer line */}
            <motion.div
                className="absolute inset-0 pointer-events-none rounded-full"
                style={{
                    background: "linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.3) 50%, transparent 80%)",
                    backgroundSize: "200% 100%",
                }}
                animate={hovered ? { backgroundPosition: ["180% 0", "-180% 0"] } : {}}
                transition={{ duration: 0.8, repeat: Infinity }}
            />

            <div
                className="relative z-10 flex items-center gap-3"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <span className="tracking-wide">{label}</span>
                <motion.div
                    className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center shadow-inner"
                    animate={{ x: isRTL ? [0, -3, 0] : [0, 3, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                >
                    <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
                </motion.div>
            </div>
        </MagneticBtn>
    );
};

// ─── Modal Tab Button ─────────────────────────────────────────────────────────
const ModalTabBtn: React.FC<{
    feature: { icon: React.ElementType; shortTitle: string; title: string; color: string };
    isActive: boolean;
    onClick: () => void;
    textAlign: string;
}> = ({ feature, isActive, onClick, textAlign }) => {
    const c = COLOR_MAP[feature.color] ?? COLOR_MAP.blue;
    const Icon = feature.icon;
    const [hovered, setHovered] = useState(false);

    return (
        <motion.button
            type="button"
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className="relative flex items-center gap-3 px-5 py-3 rounded-2xl whitespace-nowrap overflow-hidden flex-shrink-0 cursor-pointer transition-all duration-300"
            style={{
                background: isActive ? "rgba(255,255,255,0.95)" : hovered ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.4)",
                border: `1.5px solid ${isActive ? c.border : "rgba(226,232,240,0.6)"}`,
                boxShadow: isActive ? `0 8px 24px -4px ${c.glow}` : "none",
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
        >
            <ShimmerLine active={isActive} color={c.from} />

            <div
                className="p-2.5 rounded-xl flex-shrink-0 transition-colors"
                style={{
                    background: isActive ? `linear-gradient(135deg, ${c.from}20, ${c.to}20)` : "rgba(100,116,139,0.08)",
                }}
            >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5 transition-colors" style={{ color: isActive ? c.from : "#64748b" }} />
            </div>

            <div className={textAlign}>
                <span className="block text-xs sm:text-sm font-bold transition-colors" style={{ color: isActive ? "#0f172a" : "#475569" }}>
                    {feature.shortTitle}
                </span>
                <span className="hidden sm:block text-[11px] text-slate-400">
                    {feature.title}
                </span>
            </div>

            {isActive && (
                <motion.div
                    layoutId="activeTabPill"
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    style={{ border: `1.5px solid ${c.from}` }}
                    transition={{ type: "spring", bounce: 0.2, duration: 0.45 }}
                />
            )}
        </motion.button>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const HealthLocationInnovation: React.FC = () => {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const ns = "HealthLocationInnovation";
        const resources = { fa, en, ar, tr } as const;
        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) i18n.addResourceBundle(lng, ns, data, true, true);
        });
    }, [i18n]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const textAlign = isRTL ? "text-right" : "text-left";
    const dir = isRTL ? "rtl" : "ltr";

    const services = [
        {
            icon: Heart,
            title: t("services.0.title", { ns: "HealthLocationInnovation" }),
            shortDesc: t("services.0.shortDesc", { ns: "HealthLocationInnovation" }),
            gradient: "from-rose-500 to-pink-500",
            glowColor: "rose",
            accentColor: "#f43f5e"
        },
        {
            icon: Zap,
            title: t("services.1.title", { ns: "HealthLocationInnovation" }),
            shortDesc: t("services.1.shortDesc", { ns: "HealthLocationInnovation" }),
            gradient: "from-amber-500 to-orange-500",
            glowColor: "amber",
            accentColor: "#f59e0b"
        },
        {
            icon: Shield,
            title: t("services.2.title", { ns: "HealthLocationInnovation" }),
            shortDesc: t("services.2.shortDesc", { ns: "HealthLocationInnovation" }),
            gradient: "from-emerald-500 to-teal-500",
            glowColor: "emerald",
            accentColor: "#10b981"
        },
    ];

    const indooraFeatures = [
        {
            id: 1,
            icon: Navigation,
            color: "blue",
            shortTitle: t("features.0.shortTitle", { ns: "HealthLocationInnovation" }),
            title: t("features.0.title", { ns: "HealthLocationInnovation" }),
            subtitle: t("features.0.subtitle", { ns: "HealthLocationInnovation" }),
            features: t("features.0.features", { ns: "HealthLocationInnovation", returnObjects: true }) as string[],
            useCases: t("features.0.useCases", { ns: "HealthLocationInnovation", returnObjects: true }) as string[]
        },
        {
            id: 2,
            icon: Package,
            color: "purple",
            shortTitle: t("features.1.shortTitle", { ns: "HealthLocationInnovation" }),
            title: t("features.1.title", { ns: "HealthLocationInnovation" }),
            subtitle: t("features.1.subtitle", { ns: "HealthLocationInnovation" }),
            features: t("features.1.features", { ns: "HealthLocationInnovation", returnObjects: true }) as string[],
            useCases: t("features.1.useCases", { ns: "HealthLocationInnovation", returnObjects: true }) as string[]
        },
        {
            id: 3,
            icon: Users,
            color: "orange",
            shortTitle: t("features.2.shortTitle", { ns: "HealthLocationInnovation" }),
            title: t("features.2.title", { ns: "HealthLocationInnovation" }),
            subtitle: t("features.2.subtitle", { ns: "HealthLocationInnovation" }),
            features: t("features.2.features", { ns: "HealthLocationInnovation", returnObjects: true }) as string[],
            useCases: t("features.2.useCases", { ns: "HealthLocationInnovation", returnObjects: true }) as string[]
        },
        {
            id: 4,
            icon: Target,
            color: "green",
            shortTitle: t("features.3.shortTitle", { ns: "HealthLocationInnovation" }),
            title: t("features.3.title", { ns: "HealthLocationInnovation" }),
            subtitle: t("features.3.subtitle", { ns: "HealthLocationInnovation" }),
            features: t("features.3.features", { ns: "HealthLocationInnovation", returnObjects: true }) as string[],
            useCases: t("features.3.useCases", { ns: "HealthLocationInnovation", returnObjects: true }) as string[]
        },
    ];

    return (
        <section
            id="health-location-innovation"
            className="w-full relative overflow-hidden py-16 sm:py-20"
            dir={dir}
            style={{ background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #f8fafc 100%)" }}
        >
            {/* Ambient Background Glows */}
            <div className="absolute top-10 left-1/4 w-96 h-96 bg-blue-400/8 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-cyan-400/8 rounded-full blur-3xl pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Header */}
                <div className="text-center mb-12 sm:mb-14">
                    <motion.div
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 bg-blue-50/90 border border-blue-200/80 shadow-sm"
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <Sparkles className="w-4 h-4 text-[#0072CE] animate-pulse" />
                        <span className="text-xs sm:text-sm font-bold text-[#0072CE]">
                            {t("header.tag", { ns: "HealthLocationInnovation" })}
                        </span>
                    </motion.div>

                    <SectionHeader
                        regularText={t("header.regularText", { ns: "HealthLocationInnovation" })}
                        highlightedText={t("header.highlightedText", { ns: "HealthLocationInnovation" })}
                    />

                    <motion.p
                        className={`text-slate-600 text-sm sm:text-base max-w-3xl mx-auto mt-3 leading-relaxed ${textAlign}`}
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                    >
                        {t("header.description", { ns: "HealthLocationInnovation" })}
                    </motion.p>
                </div>

                {/* Tech Ecosystem Row (Delband Card + Connecting Synergy Hub + Indoora Card) */}
                <div className="max-w-5xl mx-auto mb-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-stretch">
                        {/* Delband Card */}
                        <div className="md:col-span-5">
                            <TechCard
                                type="delband"
                                title={t("technologies.delband.title", { ns: "HealthLocationInnovation" })}
                                subtitle={t("technologies.delband.subtitle", { ns: "HealthLocationInnovation" })}
                                description={t("technologies.delband.description", { ns: "HealthLocationInnovation" })}
                                textAlign={textAlign}
                                t={t}
                            />
                        </div>

                        {/* Center Fusion Node */}
                        <div className="md:col-span-2 flex flex-col items-center justify-center relative py-4 md:py-0">
                            {/* Horizontal connecting lines for desktop */}
                            <div className="hidden md:block absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] bg-gradient-to-r from-rose-400/40 via-blue-500 to-sky-400/40 z-0" />

                            <motion.div
                                className="relative z-10"
                                whileHover={{ scale: 1.08 }}
                                transition={{ type: "spring", stiffness: 300, damping: 15 }}
                            >
                                <PulseRings color="rgba(0,114,206,0.3)" count={3} />
                                <div
                                    className="relative rounded-full p-4 shadow-xl overflow-hidden flex items-center justify-center bg-white/95 border-2 border-blue-100 backdrop-blur-xl"
                                    style={{ width: 80, height: 80 }}
                                >
                                    <img
                                        src="/media/Logo1.svg"
                                        alt="Delband Logo"
                                        className="w-12 h-12 object-contain relative z-10"
                                    />
                                </div>
                            </motion.div>
                            <span className="text-[11px] font-bold text-slate-500 mt-2 tracking-wide uppercase">
                                Synergy Hub
                            </span>
                        </div>

                        {/* Indoora Card */}
                        <div className="md:col-span-5">
                            <TechCard
                                type="indoora"
                                title={t("technologies.indoora.title", { ns: "HealthLocationInnovation" })}
                                subtitle={t("technologies.indoora.subtitle", { ns: "HealthLocationInnovation" })}
                                description={t("technologies.indoora.description", { ns: "HealthLocationInnovation" })}
                                textAlign={textAlign}
                                t={t}
                            />
                        </div>
                    </div>
                </div>

                {/* 3 Value Pillar Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12 max-w-5xl mx-auto">
                    {services.map((s, i) => (
                        <ServiceCard key={i} service={s} index={i} isRTL={isRTL} textAlign={textAlign} />
                    ))}
                </div>

                {/* CTA Action */}
                <motion.div
                    className="flex justify-center"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <CTAButton
                        label={t("ctaButton", { ns: "HealthLocationInnovation" })}
                        isRTL={isRTL}
                        onClick={() => setIsModalOpen(true)}
                    />
                </motion.div>
            </div>

            {/* ─── Redesigned Glass Modal ─────────────────────────────────────── */}
            {typeof document !== "undefined" && createPortal(
                <AnimatePresence>
                    {isModalOpen && (
                        <motion.div
                            id="indoora-solutions-modal-backdrop"
                            className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6"
                            style={{
                                background: "rgba(15, 23, 42, 0.65)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                            }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsModalOpen(false)}
                        >
                            <motion.div
                                id="indoora-solutions-modal-content"
                                className="relative w-full max-w-5xl h-auto max-h-[92vh] overflow-hidden rounded-3xl sm:rounded-[2rem] flex flex-col shadow-2xl border border-white/80"
                                style={{
                                    background: "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.94) 100%)",
                                    backdropFilter: "blur(32px)",
                                    WebkitBackdropFilter: "blur(32px)",
                                    boxShadow: "0 25px 60px -15px rgba(0, 114, 206, 0.25), 0 0 0 1px rgba(255,255,255,0.8) inset",
                                }}
                                initial={{ scale: 0.94, opacity: 0, y: 30 }}
                                animate={{ scale: 1, opacity: 1, y: 0 }}
                                exit={{ scale: 0.94, opacity: 0, y: 30 }}
                                transition={{ type: "spring", damping: 26, stiffness: 280 }}
                                onClick={(e) => e.stopPropagation()}
                                dir={dir}
                            >
                                {/* Ambient Background Glows */}
                                <div className="absolute top-0 right-0 w-80 h-80 bg-blue-400/10 rounded-full blur-3xl pointer-events-none" />
                                <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-400/10 rounded-full blur-3xl pointer-events-none" />

                                {/* Close Button */}
                                <button
                                    id="close-indoora-modal-btn"
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className={`absolute top-4 ${isRTL ? "left-4" : "right-4"} z-50 w-10 h-10 rounded-2xl flex items-center justify-center bg-white/80 border border-slate-200 shadow-sm hover:bg-rose-50 hover:border-rose-200 hover:text-rose-600 text-slate-500 transition-all cursor-pointer`}
                                >
                                    <X className="w-5 h-5" />
                                </button>

                                {/* Modal Header */}
                                <div className="shrink-0 px-6 sm:px-8 py-6 border-b border-slate-100 bg-white/50">
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 rounded-2xl bg-gradient-to-br from-[#0072CE] to-[#06b6d4] text-white shadow-md shadow-blue-500/20">
                                            <Navigation className="w-6 h-6" />
                                        </div>
                                        <div className={`${isRTL ? "pr-8 sm:pr-0" : "pl-8 sm:pl-0"}`}>
                                            <h2 className={`text-xl sm:text-2xl font-extrabold text-slate-900 ${textAlign}`}>
                                                {t("modal.title", { ns: "HealthLocationInnovation" })}
                                            </h2>
                                            <p className={`text-xs sm:text-sm text-slate-500 flex items-center gap-2 mt-0.5 ${textAlign}`}>
                                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                                                <span>{t("modal.subtitle", { ns: "HealthLocationInnovation" })}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Tabs Navigation */}
                                <div className="shrink-0 px-6 sm:px-8 pt-4 pb-2 border-b border-slate-100/80 bg-slate-50/50">
                                    <div className="flex gap-2.5 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
                                        {indooraFeatures.map((f, i) => (
                                            <ModalTabBtn
                                                key={i}
                                                feature={f}
                                                isActive={activeTab === i}
                                                onClick={() => setActiveTab(i)}
                                                textAlign={textAlign}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* Tab Body Content */}
                                <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 custom-scrollbar">
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={activeTab}
                                            initial={{ opacity: 0, y: 16 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -16 }}
                                            transition={{ duration: 0.28, ease: "easeOut" }}
                                        >
                                            {(() => {
                                                const feature = indooraFeatures[activeTab];
                                                const Icon = feature.icon;
                                                const c = COLOR_MAP[feature.color] ?? COLOR_MAP.blue;

                                                return (
                                                    <div className="space-y-6">
                                                        {/* Feature Hero Card */}
                                                        <div
                                                            className="relative overflow-hidden rounded-3xl p-6 sm:p-7 border transition-all"
                                                            style={{
                                                                background: `linear-gradient(135deg, ${c.lightBg} 0%, rgba(255,255,255,0.9) 100%)`,
                                                                borderColor: c.border,
                                                            }}
                                                        >
                                                            <div className="flex flex-col sm:flex-row items-start gap-5">
                                                                <div
                                                                    className="p-4 sm:p-5 rounded-2xl text-white shadow-lg shrink-0"
                                                                    style={{
                                                                        background: `linear-gradient(135deg, ${c.from}, ${c.to})`,
                                                                        boxShadow: `0 8px 24px ${c.glow}`,
                                                                    }}
                                                                >
                                                                    <Icon className="w-8 h-8 sm:w-10 sm:h-10" />
                                                                </div>

                                                                <div className="flex-1">
                                                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                                                        <span
                                                                            className="text-xs font-bold px-3 py-1 rounded-full border shadow-sm"
                                                                            style={{ background: c.bg, color: c.text, borderColor: c.border }}
                                                                        >
                                                                            {t("modal.platformBadge", { ns: "HealthLocationInnovation" })}
                                                                        </span>
                                                                        <span className="text-xs font-bold px-3 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                                                                            {t("modal.delbandBadge", { ns: "HealthLocationInnovation" })}
                                                                        </span>
                                                                    </div>

                                                                    <h3 className={`text-xl sm:text-2xl font-extrabold text-slate-900 mb-1.5 ${textAlign}`}>
                                                                        {feature.title}
                                                                    </h3>
                                                                    <p className={`text-sm text-slate-600 leading-relaxed ${textAlign}`}>
                                                                        {feature.subtitle}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* 2-Column Split: Key Capabilities & Use Cases */}
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                            {/* Key Features */}
                                                            <div className="rounded-3xl p-6 bg-white/80 border border-slate-200/80 shadow-sm flex flex-col justify-between">
                                                                <div>
                                                                    <div className="flex items-center gap-2.5 mb-4 text-slate-900">
                                                                        <div
                                                                            className="w-8 h-8 rounded-xl flex items-center justify-center text-white"
                                                                            style={{ background: `linear-gradient(135deg, ${c.from}, ${c.to})` }}
                                                                        >
                                                                            <CheckCircle2 className="w-4 h-4" />
                                                                        </div>
                                                                        <h4 className={`text-base font-bold ${textAlign}`}>
                                                                            {t("modal.keyFeatures", { ns: "HealthLocationInnovation" })}
                                                                        </h4>
                                                                    </div>

                                                                    <div className="space-y-2.5">
                                                                        {feature.features.map((item, idx) => (
                                                                            <div
                                                                                key={idx}
                                                                                className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50/80 border border-slate-100 hover:bg-blue-50/60 hover:border-blue-200/60 transition-colors"
                                                                            >
                                                                                <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: c.from }} />
                                                                                <span className={`text-xs sm:text-sm font-medium text-slate-700 ${textAlign}`}>
                                                                                    {item}
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Use Cases */}
                                                            <div className="rounded-3xl p-6 bg-white/80 border border-slate-200/80 shadow-sm flex flex-col justify-between">
                                                                <div>
                                                                    <div className="flex items-center gap-2.5 mb-4 text-slate-900">
                                                                        <div className="w-8 h-8 rounded-xl bg-amber-500 text-white flex items-center justify-center shadow-sm">
                                                                            <Lightbulb className="w-4 h-4" />
                                                                        </div>
                                                                        <h4 className={`text-base font-bold ${textAlign}`}>
                                                                            {t("modal.useCases", { ns: "HealthLocationInnovation" })}
                                                                        </h4>
                                                                    </div>

                                                                    <div className="space-y-2.5">
                                                                        {feature.useCases.map((uc, idx) => (
                                                                            <div
                                                                                key={idx}
                                                                                className="flex items-center gap-3 p-3 rounded-2xl bg-amber-50/60 border border-amber-100 hover:bg-amber-100/70 transition-colors"
                                                                            >
                                                                                <div className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                                                                                <span className={`text-xs sm:text-sm font-medium text-slate-700 ${textAlign}`}>
                                                                                    {uc}
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </motion.div>
                                    </AnimatePresence>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: rgba(241,245,249,0.6); border-radius: 99px; margin: 6px 0; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #0072CE; border-radius: 99px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #005a9c; }
            `}</style>
        </section>
    );
};

export default HealthLocationInnovation;
